import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { Package, Plus, Edit, Trash2, DollarSign, Clock, Star, Users, ArrowLeft, Sparkles } from 'lucide-react'
import { useAdminAuth } from '../../contexts/AdminAuthContext'
import {
  fetchPackages as loadPackages,
  createPackage,
  updatePackage,
  deletePackage as removePackage,
  seedDefaultPackages,
} from '../../lib/instantData'
import { brandAlert, brandConfirm } from '../../contexts/DialogContext'
import GlassCard from '../../components/ui/GlassCard'
import PanelShell from '../../components/ui/PanelShell'

interface FitnessPackage {
  id: string
  name: string
  description: string
  price: number
  duration_days: number
  features: string[]
  package_type: 'in_person' | 'online' | 'meal_plan'
  is_active: boolean
  created_at: string
  sessions_included?: number
}

const AdminPackages: React.FC = () => {
  const { adminUser } = useAdminAuth()
  const navigate = useNavigate()
  const [packages, setPackages] = useState<FitnessPackage[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingPackage, setEditingPackage] = useState<FitnessPackage | null>(null)
  const [filterType, setFilterType] = useState('all')
  const [seeding, setSeeding] = useState(false)

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    duration_days: '',
    features: [''],
    package_type: 'in_person' as 'in_person' | 'online' | 'meal_plan',
    sessions_included: '',
    is_active: true
  })

  useEffect(() => {
    fetchPackages()
  }, [])

  const fetchPackages = async () => {
    try {
      const data = await loadPackages(false)
      setPackages(data.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')))
    } catch (error) {
      console.error('Error fetching packages:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSeedDefaults = async () => {
    setSeeding(true)
    try {
      const created = await seedDefaultPackages()
      if (created > 0) await fetchPackages()
      void brandAlert({
        title: created > 0 ? 'Packages added' : 'Nothing to add',
        message:
          created > 0
            ? `Added ${created} program${created === 1 ? '' : 's'} from your website: Strong Again Method, Champion's Blueprint, The Pro Look, Elite Competitive Edge and Nutrition Blueprint.`
            : 'Every program from your website is already listed here.',
        variant: 'success',
      })
    } catch (error) {
      console.error('Error adding default packages:', error)
      void brandAlert({
        title: 'Something went wrong',
        message: 'Could not add the standard packages. Please try again.',
        variant: 'error',
      })
    } finally {
      setSeeding(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const packageData = {
      name: formData.name,
      description: formData.description,
      price: parseFloat(formData.price),
      durationDays: parseInt(formData.duration_days),
      features: formData.features.filter((feature) => feature.trim() !== ''),
      packageType: formData.package_type,
      sessionsIncluded: formData.sessions_included
        ? parseInt(formData.sessions_included)
        : undefined,
      isActive: formData.is_active,
    }

    try {
      if (editingPackage) {
        await updatePackage(editingPackage.id, packageData)
        setPackages(
          packages.map((pkg) =>
            pkg.id === editingPackage.id
              ? {
                  ...pkg,
                  name: packageData.name,
                  description: packageData.description,
                  price: packageData.price,
                  duration_days: packageData.durationDays,
                  features: packageData.features,
                  package_type: packageData.packageType,
                  sessions_included: packageData.sessionsIncluded,
                  is_active: packageData.isActive,
                }
              : pkg
          )
        )
      } else {
        await createPackage(packageData)
        await fetchPackages()
      }

      resetForm()
      setShowCreateModal(false)
      setEditingPackage(null)
    } catch (error) {
      console.error('Error saving package:', error)
    }
  }

  const handleEdit = (pkg: FitnessPackage) => {
    setEditingPackage(pkg)
    setFormData({
      name: pkg.name,
      description: pkg.description,
      price: pkg.price.toString(),
      duration_days: pkg.duration_days.toString(),
      features: pkg.features.length > 0 ? pkg.features : [''],
      package_type: pkg.package_type,
      sessions_included: pkg.sessions_included?.toString() || '',
      is_active: pkg.is_active
    })
    setShowCreateModal(true)
  }

  const handleDelete = async (packageId: string) => {
    const ok = await brandConfirm({
      title: 'Delete package?',
      message: 'Are you sure you want to delete this package? This cannot be undone.',
      variant: 'danger',
      confirmLabel: 'Delete',
      cancelLabel: 'Keep package',
    })
    if (ok) {
      try {
        await removePackage(packageId)
        setPackages(packages.filter((pkg) => pkg.id !== packageId))
      } catch (error) {
        console.error('Error deleting package:', error)
      }
    }
  }

  const togglePackageStatus = async (packageId: string, isActive: boolean) => {
    try {
      await updatePackage(packageId, { isActive: !isActive })
      setPackages(
        packages.map((pkg) =>
          pkg.id === packageId ? { ...pkg, is_active: !isActive } : pkg
        )
      )
    } catch (error) {
      console.error('Error updating package status:', error)
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      duration_days: '',
      features: [''],
      package_type: 'in_person',
      sessions_included: '',
      is_active: true
    })
  }

  const addFeature = () => {
    setFormData({ ...formData, features: [...formData.features, ''] })
  }

  const removeFeature = (index: number) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index)
    })
  }

  const updateFeature = (index: number, value: string) => {
    const newFeatures = [...formData.features]
    newFeatures[index] = value
    setFormData({ ...formData, features: newFeatures })
  }

  const filteredPackages = packages.filter(pkg => {
    if (filterType === 'all') return true
    return pkg.package_type === filterType
  })

  const getPackageTypeColor = (type: string) => {
    switch (type) {
      case 'in_person': return 'bg-green-100 text-green-800'
      case 'online': return 'bg-blue-100 text-blue-800'
      case 'meal_plan': return 'bg-purple-100 text-purple-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPackageTypeLabel = (type: string) => {
    switch (type) {
      case 'in_person': return 'In-Person Training'
      case 'online': return 'Online Training'
      case 'meal_plan': return 'Meal Plan'
      default: return type
    }
  }

  if (loading) {
    return (
      <div className="panel-page flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gold"></div>
      </div>
    )
  }

  return (
    <div className="panel-page">
      <div className="panel-container">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="mb-4 flex items-center text-gray-600 hover:text-ink transition-colors"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Return to Admin Dashboard
              </button>
              <p className="eyebrow mb-3">Admin</p>
              <h1 className="panel-title flex items-center">
                <Package className="mr-3 h-8 w-8" />
                Package Management
              </h1>
              <p className="mt-2 text-gray-600">
                Create and manage fitness packages and pricing
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleSeedDefaults}
                disabled={seeding}
                className="border border-gray-300 bg-white text-gray-700 px-4 py-2 rounded-lg hover:bg-cream transition-colors flex items-center disabled:opacity-50"
              >
                <Sparkles className="mr-2 h-4 w-4" />
                {seeding ? 'Adding…' : 'Add website programs'}
              </button>
              <button
                onClick={() => {
                  resetForm()
                  setEditingPackage(null)
                  setShowCreateModal(true)
                }}
                className="bg-ink text-white px-4 py-2 rounded-lg hover:bg-ink-soft transition-colors flex items-center"
              >
                <Plus className="mr-2 h-4 w-4" />
                Create Package
              </button>
            </div>
          </div>
        </motion.div>

        {/* Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-6"
        >
          <div className="flex space-x-4">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="pl-4 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent appearance-none"
            >
              <option value="all">All Package Types</option>
              <option value="in_person">In-Person Training</option>
              <option value="online">Online Training</option>
              <option value="meal_plan">Meal Plans</option>
            </select>
          </div>
        </motion.div>

        {/* Packages Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {filteredPackages.map((pkg, index) => (
            <motion.div
              key={pkg.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <GlassCard className="p-6 h-full">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-display text-lg font-semibold text-ink">{pkg.name}</h3>
                    <span className={`inline-block px-2 py-1 text-xs rounded-full mt-2 ${getPackageTypeColor(pkg.package_type)}`}>
                      {getPackageTypeLabel(pkg.package_type)}
                    </span>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(pkg)}
                      className="p-2 text-gray-400 hover:text-brand transition-colors"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(pkg.id)}
                      className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="mb-4">
                  <div className="flex items-center font-display text-2xl font-semibold text-ink mb-2">
                    <DollarSign className="h-6 w-6" />
                    {pkg.price}
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Clock className="h-4 w-4 mr-1" />
                    {pkg.duration_days} days
                    {pkg.sessions_included && (
                      <span className="ml-4 flex items-center">
                        <Users className="h-4 w-4 mr-1" />
                        {pkg.sessions_included} sessions
                      </span>
                    )}
                  </div>
                </div>

                <p className="text-gray-600 text-sm mb-4 line-clamp-3">{pkg.description}</p>

                <div className="mb-4">
                  <h4 className="font-medium text-ink mb-2 flex items-center">
                    <Star className="h-4 w-4 mr-1" />
                    Features
                  </h4>
                  <ul className="space-y-1">
                    {pkg.features.slice(0, 3).map((feature, idx) => (
                      <li key={idx} className="text-sm text-gray-600 flex items-center">
                        <span className="w-1.5 h-1.5 bg-ink rounded-full mr-2"></span>
                        {feature}
                      </li>
                    ))}
                    {pkg.features.length > 3 && (
                      <li className="text-sm text-gray-500">
                        +{pkg.features.length - 3} more features
                      </li>
                    )}
                  </ul>
                </div>

                <div className="flex items-center justify-between mt-auto">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    pkg.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {pkg.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <button
                    onClick={() => togglePackageStatus(pkg.id, pkg.is_active)}
                    className={`px-3 py-1 text-sm rounded transition-colors ${
                      pkg.is_active 
                        ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                  >
                    {pkg.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                </div>
              </GlassCard>
            </motion.div>
          ))}
        </motion.div>

        {filteredPackages.length === 0 && (
          <div className="text-center py-12">
            <Package className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No packages found</p>
            {packages.length === 0 && (
              <>
                <p className="text-gray-500 text-sm mt-2 max-w-md mx-auto">
                  Add the programs already advertised on your website so you can assign them to clients.
                </p>
                <button
                  onClick={handleSeedDefaults}
                  disabled={seeding}
                  className="mt-4 inline-flex items-center bg-ink text-white px-4 py-2 rounded-lg hover:bg-ink-soft transition-colors disabled:opacity-50"
                >
                  <Sparkles className="mr-2 h-4 w-4" />
                  {seeding ? 'Adding…' : 'Add website programs'}
                </button>
              </>
            )}
          </div>
        )}

        {/* Create/Edit Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-xl font-bold text-ink mb-4">
                  {editingPackage ? 'Edit Package' : 'Create New Package'}
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Package Name</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Package Type</label>
                      <select
                        value={formData.package_type}
                        onChange={(e) => setFormData({ ...formData, package_type: e.target.value as any })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                      >
                        <option value="in_person">In-Person Training</option>
                        <option value="online">Online Training</option>
                        <option value="meal_plan">Meal Plan</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Price ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        required
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Duration (days)</label>
                      <input
                        type="number"
                        required
                        value={formData.duration_days}
                        onChange={(e) => setFormData({ ...formData, duration_days: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Sessions Included</label>
                      <input
                        type="number"
                        value={formData.sessions_included}
                        onChange={(e) => setFormData({ ...formData, sessions_included: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                        placeholder="Optional"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      required
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Features</label>
                    {formData.features.map((feature, index) => (
                      <div key={index} className="flex space-x-2 mb-2">
                        <input
                          type="text"
                          value={feature}
                          onChange={(e) => updateFeature(index, e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                          placeholder="Feature description"
                        />
                        {formData.features.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeFeature(index)}
                            className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addFeature}
                      className="text-brand hover:text-brand-dark text-sm font-medium"
                    >
                      + Add Feature
                    </button>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="rounded border-gray-300 text-brand focus:ring-brand"
                    />
                    <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                      Package is active and available for purchase
                    </label>
                  </div>

                  <div className="flex justify-end space-x-4 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateModal(false)
                        setEditingPackage(null)
                        resetForm()
                      }}
                      className="btn-secondary text-sm px-5 py-2.5"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-gold text-sm px-5 py-2.5 rounded-full hover:bg-ink-soft transition-colors"
                    >
                      {editingPackage ? 'Update Package' : 'Create Package'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminPackages 